import {Component, effect, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatIconButton} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {VulcanIconComponent} from '@eagle-i/vulcan-component-library';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import {map} from 'rxjs';
import {GenIceCountySummary} from '../../../../../../../generated/serverModels/GenIceCountySummary';
import {IceLayerFeature, IceService} from '../../../../../../../services/ice.service';
import {BaseStateReportWidget} from '../base-state-report-widget';
import {GenIceSector} from "../../../../../../../generated/serverModels/GenIceSector";


interface RowData {
  rank: number,
  totalEntry: IceLayerFeature,
  perCustomerEventEntry: IceLayerFeature,
}

@Component({
  selector: 'tgrid-economic-cost-ranking-widget',
  imports: [
    MatCardModule,
    MatIconButton,
    VulcanIconComponent,
    MatTableModule,
  ],
  templateUrl: './economic-cost-ranking-widget.component.html',
  standalone: true,
  styleUrls: ['./economic-cost-ranking-widget.component.scss'],
})
export class EconomicCostRankingWidgetComponent extends BaseStateReportWidget {
  private iceService = inject(IceService);
  readonly displayedColumns = [
    'rank',
    'totalCost',
    'costPerCustomerEvent',
  ];


  protected tableSource = new MatTableDataSource<RowData>();

  constructor() {
    effect(() => {
      const year = this.yearParam();
      const state = this.stateParam();
      const selectedState = this.territories().find(t => t.abbreviation === state)!;
      if(!selectedState) {
        return;
      }

      this.iceService.getCountyCostData([year], [selectedState], GenIceSector.ALL, true );
    });


    super();
    this.iceService.countyCostData$
      .pipe(
        map(costData => {

          function getTotalCost(f: { properties: GenIceCountySummary; }) {
            return f.properties.totalCost;
          }

          function getCostPerCustomerEvent(f: { properties: GenIceCountySummary; }) {
            return f.properties.costPerCustomerEvent;
          }

          const stopIndex = Math.min(10, costData.features.length);
          const totalSortedList = costData.features.sort((a, b) => getTotalCost(b) - getTotalCost(a)).slice(0, stopIndex);
          const perCustomerEventSortedList = costData.features.sort((a, b) => getCostPerCustomerEvent(b) - getCostPerCustomerEvent(a)).slice(0, stopIndex);
          const rows: RowData[] = [];
          for (let i = 0; i < stopIndex; i++) {
            //adding full objects in case we want to show more details in the future, currently only showing rank and cost values
            rows.push({
              rank: i + 1,
              totalEntry: totalSortedList[i],
              perCustomerEventEntry: perCustomerEventSortedList[i],
            });
          }
          return rows;
        }),
        takeUntilDestroyed(),
      )
      .subscribe(costMetrics => {
        this.tableSource.data = costMetrics || [];
      });
  }

  protected override async export(): Promise<void> {
    const csv = this.tableSource.data!.map(row => {
      return [row.rank, row.perCustomerEventEntry.properties.name, row.totalEntry.properties.name];
    }).reduce((csvString, row) => {
      return csvString + row.join(',') + '\n';
    }, 'Rank,Highest Per Customer Cost County,Highest Total Cost County\n');

    const filename = `economic_cost_ranking_${this.stateParam()}_${this.yearParam()}`;
    const zip = new JSZip();

    const csvBlob = new Blob([csv], {type: 'text/csv'});
    zip.file(`${filename}.txt`, csvBlob);

    try {
      const canvas = await html2canvas(this.exportContent()?.nativeElement);
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve));
      if (blob) {
        zip.file(`${filename}.png`, blob);
        const content = await zip.generateAsync({type: 'blob'});
        const url = URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.zip`;
        link.click();
        URL.revokeObjectURL(url);
      }
      this.snackBar.open('Export downloaded successfully', 'Close', {duration: 3000});
    } catch (err) {
      console.error(err);
      this.snackBar.open('Export failed. Please try again.', 'Close', {duration: 3000});
    }
  }
}
